/**
 * planConfig.js — single source of truth for plan keys, tiers and limits.
 *
 * MASTER copy: Overarching Resources/planConfig/planConfig.js
 * Synced (byte-identical) by sync_plan_config.sh to:
 *   - Backend/Firebase DEV/functions/planConfig/
 *   - Backend/!!!___Firebase PROD/functions/planConfig/
 *   - Backend/Cloud Run Services/{dashboard,surveyAPI,reviewsAndShowcase}/sharedMethods/planConfig/
 *   - Frontend/Dashboard App/src/planConfig/   (planConfig.js only)
 * NEVER edit a copy directly — edit the master and re-run the sync script.
 *
 * Constraints on this file:
 *   - ZERO imports/requires. Pure data + pure functions only.
 *   - CommonJS exports (module.exports) — the same bytes must run under
 *     require() in Node 14 services/functions AND through the Vue 2 webpack
 *     build (which consumes CommonJS modules fine).
 *   - returnJS / returnBusinessData get NO copy by design: serve paths read
 *     only stored derived fields (showFBLLogo, gateLocked), never resolve plans.
 *
 * Plan model (2026 pricing — "Free Until It Works"):
 *   New keys: free, solo_m/solo_y ($9/$90), auto_m/auto_y ($29/$290),
 *             pro_m/pro_y ($79/$790).
 *   Legacy keys kept on entities.stripePlan: 1m/1y (Learn), 2m/2y (Engage),
 *   3m/3y (Soar), 4m/4y (Streamline), as/dfy/fbl/pg (LTDs), trial.
 *   Legacy keys resolve to their mapped tier, EXCEPT where today's live limits
 *   differ — those keep today's exact numbers via LEGACY_LIMIT_OVERRIDES
 *   (grandfathered subs and pre-migration trials must see zero change).
 */

//master kill switch for the outcome blur-gate (Phase 4+). Flipping this to
//false makes every plan resolve outcomesUnlocked/importedReviews = Infinity.
var OUTCOME_GATE_ENABLED = true

//------------------------------------------------------------------
// Tier limit tables (the NEW model). multisAllowed counts ADDITIONAL
// spaces/locations beyond the first (matches getNumberOfMultisAllowed
// semantics: 0 = single space, false = adding blocked entirely).
//
// branding (2026-07 model): true = "Powered by Feedback Link" LOCKED ON
// with the strong (badge) style — free tier, no opt-out. false = branding
// still DEFAULTS ON but subtle (grey link), and the entity may opt out via
// entities.fblBrandingOff (written by the dashboard svc setBranding
// endpoint). Derivation lives in derivedFields.js.
//------------------------------------------------------------------
var TIER_LIMITS = {
  free: {
    invitesPerMonth: 20,          //email + SMS, one pool
    outcomesUnlocked: 20,         //then blur-gate
    importedReviews: 10,          //extension/import captures, then gate
    videoLimit: 5,
    multisAllowed: 0,
    collaborators: true,          //all plans incl. free (staff QR validation)
    collabRestricted: false,
    byoSending: true,             //2026-07-06: BYO on Free too — Rev-4 meters BYO against the same invite pool (nothing bypassed); own-domain deliverability + keeps free senders off shared reputation
    devAPI: false,
    branding: true,               //branding LOCKED ON + strong style (no opt-out)
    customDomain: true,           //2026-07-07 (owner, revising same-day paid-only decision): self-serve custom survey domains on ALL plans incl. Free — "free to start, all features" positioning. Anti-squat now rests on TXT ownership proof + one-in-flight + 30-day expiry (see customDomain.js); the flag stays as a per-tier kill-switch if abuse appears. Serving stays plan-agnostic (keys off associatedURLs).
    fairUseSoftAlert: false,
  },
  solo: {
    invitesPerMonth: 100,
    outcomesUnlocked: Infinity,   //paid = unlimited outcomes; cap is invites+video
    importedReviews: Infinity,
    videoLimit: 50,
    multisAllowed: 0,
    collaborators: true,
    collabRestricted: false,
    byoSending: true,
    devAPI: false,
    branding: false,
    customDomain: true,
    fairUseSoftAlert: false,
  },
  auto: {
    invitesPerMonth: 1000,
    outcomesUnlocked: Infinity,   //paid = unlimited outcomes; cap is invites+video
    importedReviews: Infinity,
    videoLimit: 200,
    multisAllowed: 4,
    collaborators: true,
    collabRestricted: false,
    byoSending: true,
    devAPI: true,
    branding: false,
    customDomain: true,
    fairUseSoftAlert: false,
  },
  pro: {
    invitesPerMonth: Infinity,    //fair use; housekeeping soft-alerts at 10k
    outcomesUnlocked: Infinity,
    importedReviews: Infinity,
    videoLimit: 1000,
    multisAllowed: 9,            //= today's Soar (3m) allowance
    collaborators: true,
    collabRestricted: false,
    byoSending: true,
    devAPI: true,
    branding: false,
    customDomain: true,
    fairUseSoftAlert: 10000,
  },
}
//customDomain notes: true on every resolution path — free base is true, so
//legacy keys (LEGACY_MAP → solo/auto/pro), pre-migration 'trial', unknown keys
//and canceled/unpaid degradation (all built from the free base) inherit true.
//The limits.customDomain checks in the dashboard svc + UI stay in place as a
//kill-switch: flip a tier back to false and its gate re-engages.

//------------------------------------------------------------------
// Plan key registry. NEW keys are the only keys Stripe webhooks write
// post-launch. LEGACY keys remain valid on entities indefinitely
// (grandfathered subs, LTDs) and resolve through LEGACY_MAP.
//------------------------------------------------------------------
var NEW_PLAN_KEYS = {
  free:   { tier: 'free', isAnnual: false, isLTD: false },
  solo_m: { tier: 'solo', isAnnual: false, isLTD: false },
  solo_y: { tier: 'solo', isAnnual: true,  isLTD: false },
  auto_m: { tier: 'auto', isAnnual: false, isLTD: false },
  auto_y: { tier: 'auto', isAnnual: true,  isLTD: false },
  pro_m:  { tier: 'pro',  isAnnual: false, isLTD: false },
  pro_y:  { tier: 'pro',  isAnnual: true,  isLTD: false },
}

var LEGACY_MAP = {
  '1m':  { tier: 'solo', isAnnual: false, isLTD: false },
  '1y':  { tier: 'solo', isAnnual: true,  isLTD: false },
  '2m':  { tier: 'auto', isAnnual: false, isLTD: false },
  '2y':  { tier: 'auto', isAnnual: true,  isLTD: false },
  '3m':  { tier: 'pro',  isAnnual: false, isLTD: false },
  '3y':  { tier: 'pro',  isAnnual: true,  isLTD: false },
  '4m':  { tier: 'pro',  isAnnual: false, isLTD: false },
  '4y':  { tier: 'pro',  isAnnual: true,  isLTD: false },
  as:    { tier: 'pro',  isAnnual: false, isLTD: true },
  dfy:   { tier: 'pro',  isAnnual: false, isLTD: true },
  fbl:   { tier: 'pro',  isAnnual: false, isLTD: true },
  pg:    { tier: 'pro',  isAnnual: false, isLTD: true },
  //processCouponDealCode.js writes the LONG names ('as' is the only short
  //key any writer produces — videoLimits' dfy/pg keys never matched real
  //entities, which silently fell to the default-50/no-multis bucket). Alias
  //both spellings so existing docs resolve as the LTDs they paid for.
  dealify:     { tier: 'pro', isAnnual: false, isLTD: true },
  pitchground: { tier: 'pro', isAnnual: false, isLTD: true },
  trial: { tier: 'free', isAnnual: false, isLTD: false },
}

//Per-key parity overrides: where TODAY'S live limits differ from the mapped
//tier, the legacy key keeps today's exact numbers. Verified 2026-06-12 against
//videoLimits.js, addNewLocation.getNumberOfMultisAllowed and
//moduleDashGetters.{numberOfMultisAllowed,haveAccessToCollab}.
var LEGACY_LIMIT_OVERRIDES = {
  //entry plans: collab gated today (haveAccessToCollab false unless override)
  '1m': { collabRestricted: true },
  '1y': { collabRestricted: true },
  //Engage keeps its 5 extra spaces (new auto tier sells 1 space)
  '2m': { multisAllowed: 5 },
  '2y': { multisAllowed: 5 },
  //Streamline: unlimited video today; adding spaces was never enabled (false,
  //not 0 — getNumberOfMultisAllowed has no 4m/4y case and falls through)
  '4m': { videoLimit: Infinity, multisAllowed: false },
  '4y': { videoLimit: Infinity, multisAllowed: false },
  //Pre-migration trials: today's trial behavior, and NO outcome gate — the
  //blur-gate applies only to entities whose key is literally 'free' (Phase 7
  //migration flips trials to 'free'; until then Phase 4 stays dark).
  trial: {
    videoLimit: 50,
    multisAllowed: 5,             //active trial; expiry handled via status
    collabRestricted: true,
    branding: false,              //trial = paid-like: default ON, subtle, may opt out
    outcomesUnlocked: Infinity,
    importedReviews: Infinity,
    invitesPerMonth: Infinity,    //trial uses legacy trialEmailQuotaLeft fields
  },
}

//Unknown/absent key with a real (non-trial) subStatusOK — today's behavior:
//video default 50, adding spaces blocked, collab unrestricted (quirk of
//haveAccessToCollab: non-trial + non-entry returns true).
var UNKNOWN_KEY_LIMITS = {
  videoLimit: 50,
  multisAllowed: false,
  outcomesUnlocked: Infinity,
  importedReviews: Infinity,
  invitesPerMonth: Infinity,
}

var DEFAULT_VIDEO_LIMIT = 50    //parity with videoLimits.js DEFAULT_VIDEO_LIMIT
var TRIAL_LENGTH_DAYS = 14      //parity with checkIsTrialOver.js / addNewLocation.js

//Display strings. Legacy quirk preserved: only the four monthly legacy keys
//ever had a planString (yearly keys showed nothing in the navbar).
var PLAN_DISPLAY_NAMES = {
  '1m': 'Learn Monthly',
  '2m': 'Engage Monthly',
  '3m': 'Soar Monthly',
  '4m': 'Streamline Monthly',
  free:   'Free',
  solo_m: 'Learn',
  solo_y: 'Learn (Annual)',
  auto_m: 'Growth',
  auto_y: 'Growth (Annual)',
  pro_m:  'Engage',
  pro_y:  'Engage (Annual)',
}

//Stripe price IDs per environment. Test created 2026-06-12 (lookup_key = plan
//key on each price). LIVE filled at launch; legacyByPriceID maps OLD price IDs
//to legacy plan keys so grandfathered subs stay webhook-synced.
var PRICE_IDS = {
  test: {
    solo_m: 'price_1ThVjBINOIluI3kBV47cb9BR',
    solo_y: 'price_1ThVjCINOIluI3kBTO0M3Ag5',
    auto_m: 'price_1ThVjDINOIluI3kBbULnAHLJ',
    auto_y: 'price_1ThVjEINOIluI3kBScGEWNM1',
    pro_m:  'price_1ThVjFINOIluI3kBMsog0Mdc',
    pro_y:  'price_1ThVjGINOIluI3kB9blREdW9',
    legacyByPriceID: {
      price_1MX0NqINOIluI3kBndyCbaXt: '1m', //Feedback Link Learn $19 (test)
      price_1MX0OAINOIluI3kBPxp0XABJ: '2m', //Feedback Link Engage $69 (test)
      price_1MX0OZINOIluI3kByvw87Hd6: '3m', //Feedback Link Soar $329 (test)
    },
  },
  live: {
    solo_m: 'price_1TodgCINOIluI3kBZxoMUEIX',
    solo_y: 'price_1TodgDINOIluI3kBUWSmPRHd',
    auto_m: 'price_1TodgEINOIluI3kBIImFK6rg',
    auto_y: 'price_1TodgFINOIluI3kBcY5ycUfe',
    pro_m:  'price_1TodgGINOIluI3kBe5kVpFaG',
    pro_y:  'price_1TodgHINOIluI3kB7LcSvMS1',
    legacyByPriceID: {
      //Learn (prod_DYxoU94vv3Ix0j) — 4 subs live on the first two as of 2026-07-02
      price_1MZEloINOIluI3kBgX367jME: '1m', //Learn $19
      price_1Hj6ARINOIluI3kBs56J0cnZ: '1m', //Learn $15 (grandfathered)
      price_1IvHLLINOIluI3kB1kL5FW7G: '1m', //Learn $9
      plan_DYxppB7aC7nt5s: '1m',            //Learn $9 (plan-era)
      //Engage (prod_DYxuo7GirOTrSF) — 1 past_due sub on the $69 price
      price_1MZEkxINOIluI3kB1qbHXJoB: '2m', //Engage $69
      price_1Hj69kINOIluI3kBmqDwNIOm: '2m', //Engage $49
      plan_DYxuz1rgUKZCuO: '2m',            //Engage $39 (plan-era)
      //Soar (prod_DYxx8lTQJAno9P) — no live subs
      price_1MZEjQINOIluI3kB63k4axxB: '3m', //Soar $329
      price_1Hj65MINOIluI3kBaFQuplmH: '3m', //Soar $299
      plan_EU92UtBSwxN8Ox: '3m',            //Soar $99 (plan-era)
    },
  },
}

//Resolve a Stripe price ID to a plan key for the active environment.
//env: 'test' | 'live'. Returns false for unknown prices (anomaly — log it).
function planKeyForPriceID (priceID, env) {
  var table = PRICE_IDS[env === 'live' ? 'live' : 'test']
  var k
  for (k in table) {
    if (k !== 'legacyByPriceID' && table[k] === priceID) return k
  }
  if (table.legacyByPriceID && Object.prototype.hasOwnProperty.call(table.legacyByPriceID, priceID)) {
    return table.legacyByPriceID[priceID]
  }
  return false
}

//------------------------------------------------------------------
// Pure helpers
//------------------------------------------------------------------

function isKnownPlanKey (key) {
  if (typeof key !== 'string') return false
  return Object.prototype.hasOwnProperty.call(NEW_PLAN_KEYS, key) ||
         Object.prototype.hasOwnProperty.call(LEGACY_MAP, key)
}

//Trial state, derived exactly like checkIsTrialOver.js / addNewLocation.js:
//missing subStatusOK field = trial; expires TRIAL_LENGTH_DAYS after dateCreated.
//Only meaningful for legacy entities — entities on NEW keys (incl. 'free')
//are never trials regardless of subStatusOK.
function deriveTrialState (entityData) {
  //planStatus is written ONLY by new-model writers (webhook, LTD redemption,
  //housekeeping, migration) — its presence overrides the legacy
  //missing-subStatusOK heuristic, so a just-redeemed LTD / just-synced sub
  //resolves correctly even before the derived subStatusOK mirror lands.
  var isTrial = typeof entityData.subStatusOK === 'undefined' &&
                typeof entityData.planStatus !== 'string'
  if (!isTrial) return { isTrial: false, isTrialEnded: false, daysLeft: false }
  var oneDay = 24 * 60 * 60 * 1000
  var diffDays = Math.round((new Date().getTime() - (entityData.dateCreated * 1000)) / oneDay)
  var daysLeft = TRIAL_LENGTH_DAYS - diffDays
  return { isTrial: true, isTrialEnded: daysLeft < 0, daysLeft: daysLeft }
}

function buildLimits (tier, overrides) {
  var base = TIER_LIMITS[tier] || TIER_LIMITS.free
  var out = {}
  var k
  for (k in base) out[k] = base[k]
  if (overrides) { for (k in overrides) out[k] = overrides[k] }
  if (!OUTCOME_GATE_ENABLED) {
    out.outcomesUnlocked = Infinity
    out.importedReviews = Infinity
  }
  return out
}

/**
 * The resolver. Everything that gates on plan should end up here.
 *
 * @param {object} entityData  the entities/{bid} doc data (or the frontend
 *                             state.viewData.business object — same shape)
 * @returns {{ planKey:string, tier:string, isLTD:boolean, isAnnual:boolean,
 *             status:string, isTrial:boolean, isTrialEnded:boolean,
 *             trialDaysLeft:(number|false), planString:(string|false),
 *             limits:object }}
 *
 * status: 'active' | 'past_due' | 'canceled' | 'none' | 'trial' | 'trial_expired'
 * Status degrade rule: planStatus 'canceled'/'unpaid' → free-tier limits with
 * planKey retained for display; 'past_due' keeps paid limits (dunning grace).
 */
function resolvePlan (entityData) {
  var data = entityData || {}
  var rawKey = typeof data.stripePlan === 'string' ? data.stripePlan : false

  //--- NEW plan keys: planStatus (webhook-written) is the authority ---
  if (rawKey && Object.prototype.hasOwnProperty.call(NEW_PLAN_KEYS, rawKey)) {
    var meta = NEW_PLAN_KEYS[rawKey]
    var status = typeof data.planStatus === 'string' ? data.planStatus : (rawKey === 'free' ? 'none' : 'active')
    var degraded = (status === 'canceled' || status === 'unpaid')
    var limits = degraded ? buildLimits('free') : buildLimits(meta.tier)
    return {
      planKey: rawKey,
      tier: degraded ? 'free' : meta.tier,
      isLTD: meta.isLTD,
      isAnnual: meta.isAnnual,
      status: status,
      isTrial: false,
      isTrialEnded: false,
      trialDaysLeft: false,
      planString: PLAN_DISPLAY_NAMES[rawKey] || false,
      limits: applyEntityOverrides(limits, data),
    }
  }

  //--- Legacy world: trial derivation comes first (matches today's gates) ---
  var trialState = deriveTrialState(data)
  if (trialState.isTrial) {
    var trialLimits = buildLimits('free', LEGACY_LIMIT_OVERRIDES.trial)
    if (trialState.isTrialEnded) trialLimits.multisAllowed = false //adding blocked
    return {
      planKey: 'trial',
      tier: 'free',
      isLTD: false,
      isAnnual: false,
      status: trialState.isTrialEnded ? 'trial_expired' : 'trial',
      isTrial: true,
      isTrialEnded: trialState.isTrialEnded,
      trialDaysLeft: trialState.daysLeft,
      planString: false,
      limits: applyEntityOverrides(trialLimits, data),
    }
  }

  //--- Legacy paid / LTD keys ---
  if (rawKey && Object.prototype.hasOwnProperty.call(LEGACY_MAP, rawKey) && rawKey !== 'trial') {
    var lmeta = LEGACY_MAP[rawKey]
    var lstatus = typeof data.planStatus === 'string' ? data.planStatus : 'active'
    var ldegraded = (lstatus === 'canceled' || lstatus === 'unpaid')
    var llimits = ldegraded
      ? buildLimits('free')
      : buildLimits(lmeta.tier, LEGACY_LIMIT_OVERRIDES[rawKey])
    return {
      planKey: rawKey,
      tier: ldegraded ? 'free' : lmeta.tier,
      isLTD: lmeta.isLTD,
      isAnnual: lmeta.isAnnual,
      status: lstatus,
      isTrial: false,
      isTrialEnded: false,
      trialDaysLeft: false,
      planString: PLAN_DISPLAY_NAMES[rawKey] || false,
      limits: applyEntityOverrides(llimits, data),
    }
  }

  //--- Unknown/absent key but subStatusOK exists (e.g. stale custom subs) ---
  return {
    planKey: rawKey || 'unknown',
    tier: 'free',
    isLTD: false,
    isAnnual: false,
    status: 'none',
    isTrial: false,
    isTrialEnded: false,
    trialDaysLeft: false,
    planString: false,
    limits: applyEntityOverrides(buildLimits('free', UNKNOWN_KEY_LIMITS), data),
  }
}

//Entity-level override precedence (preserved from today's gates):
//specialVideoLimit and specialMultisAllowed beat the plan.
//allowCollaborate is consumed by the collab getter itself (shape-sensitive).
function applyEntityOverrides (limits, data) {
  if (typeof data.specialVideoLimit !== 'undefined' && data.specialVideoLimit !== null && !isNaN(data.specialVideoLimit)) {
    limits.videoLimit = Number(data.specialVideoLimit)
  }
  if (typeof data.specialMultisAllowed !== 'undefined' && !isNaN(data.specialMultisAllowed)) {
    limits.multisAllowed = data.specialMultisAllowed
  }
  return limits
}

//------------------------------------------------------------------
// Faithful Phase-1 delegation helpers — these reproduce the EXACT
// behavior of the gates they replace (call-site signatures preserved).
//------------------------------------------------------------------

//Replaces videoLimits.js's internal table lookup. Keyed purely on stripePlan
//(no trial/expiry logic — parity with the original).
function videoLimitForKey (stripePlan, specialVideoLimit) {
  if (typeof specialVideoLimit !== 'undefined' && specialVideoLimit !== null && !isNaN(specialVideoLimit)) {
    return Number(specialVideoLimit)
  }
  if (typeof stripePlan === 'undefined' || stripePlan === null || stripePlan === false) return DEFAULT_VIDEO_LIMIT
  var key = String(stripePlan).toLowerCase()
  if (Object.prototype.hasOwnProperty.call(NEW_PLAN_KEYS, key)) {
    return buildLimits(NEW_PLAN_KEYS[key].tier).videoLimit
  }
  if (Object.prototype.hasOwnProperty.call(LEGACY_MAP, key)) {
    return buildLimits(LEGACY_MAP[key].tier, LEGACY_LIMIT_OVERRIDES[key]).videoLimit
  }
  return DEFAULT_VIDEO_LIMIT
}

//Replaces addNewLocation.getNumberOfMultisAllowed / the dashboard's
//numberOfMultisAllowed getter. Returns a number, or false when adding is
//blocked. Order of precedence preserved: trial-expired → false,
//specialMultisAllowed → override, then plan table.
function multisAllowedForEntity (entityData) {
  var resolved = resolvePlan(entityData || {})
  if (resolved.isTrialEnded) return false
  return resolved.limits.multisAllowed
}

module.exports = {
  OUTCOME_GATE_ENABLED: OUTCOME_GATE_ENABLED,
  TIER_LIMITS: TIER_LIMITS,
  NEW_PLAN_KEYS: NEW_PLAN_KEYS,
  LEGACY_MAP: LEGACY_MAP,
  LEGACY_LIMIT_OVERRIDES: LEGACY_LIMIT_OVERRIDES,
  PLAN_DISPLAY_NAMES: PLAN_DISPLAY_NAMES,
  PRICE_IDS: PRICE_IDS,
  DEFAULT_VIDEO_LIMIT: DEFAULT_VIDEO_LIMIT,
  TRIAL_LENGTH_DAYS: TRIAL_LENGTH_DAYS,
  isKnownPlanKey: isKnownPlanKey,
  resolvePlan: resolvePlan,
  videoLimitForKey: videoLimitForKey,
  multisAllowedForEntity: multisAllowedForEntity,
  planKeyForPriceID: planKeyForPriceID,
}
